import { useCallback, useEffect, useState } from "react";
import * as L from "leaflet";

import { Emission, EmissionFeatureCollection, EmissionType } from "src/types";
import * as OGCService from "src/services/ogc.service";
import { getEmissionWMSLayerName } from "src/utils/emission.utils";
import { parseWMSFeatureInfoParams } from "src/utils/ogc.utils";
import { removeAllDrawnLayers } from "src/utils/map.utils";
import { getLeafletMarkerIcon } from "src/utils/marker.utils";

export enum MapMode {
  DRAW = "DRAW",
  SELECT = "SELECT",
}

export function usePointSourceEmissionDraw(
  map: L.Map,
  emission: Emission,
  callbacks: {
    onSelect: (feature: EmissionFeatureCollection) => void;
    onDraw: (latlng: L.LatLngLiteral) => void;
  }
) {
  const [mapMode, setMapMode] = useState<MapMode>(MapMode.SELECT);

  /**
   * Callbacks
   */
  const selectCallback = useCallback(select, [map, emission, callbacks]);
  const drawCallback = useCallback(draw, [map, callbacks]);

  /**
   * Effects
   */
  useEffect(() => {
    return function cleanup() {
      removeAllDrawnLayers(map);
    };
  }, [map]);

  useEffect(() => {
    if (mapMode === MapMode.DRAW) return drawCallback();
    if (mapMode === MapMode.SELECT) return selectCallback();
  }, [drawCallback, selectCallback, mapMode]);

  /**
   * Functions
   */
  function toggleMapMode() {
    // remove drawings from map mode
    removeAllDrawnLayers(map);
    if (mapMode === MapMode.DRAW) setMapMode(MapMode.SELECT);
    if (mapMode === MapMode.SELECT) setMapMode(MapMode.DRAW);
  }

  function select() {
    map.pm.disableDraw("Marker");

    map.on("click", async (event: L.LeafletMouseEvent) => {
      removeAllDrawnLayers(map);
      const clickWMSParams = parseWMSFeatureInfoParams(event);

      const featureInfo = await OGCService.getWMSFeatureInfo<{
        shape_id: number;
      }>({
        ...clickWMSParams,
        layers: getEmissionWMSLayerName(EmissionType.POINT_SOURCE),
        query_layers: getEmissionWMSLayerName(EmissionType.POINT_SOURCE),
        viewParams: `shapecollection_id:${emission.geoserverFilter}`,
      });

      setTimeout(() => {
        callbacks.onSelect(featureInfo);
      }, 100);
    });

    return function cleanup() {
      map.off("click");
    };
  }

  function draw() {
    // @ts-ignore: incomplete type
    map.pm.setGlobalOptions({ hideMiddleMarkers: true });

    map.on("pm:create", (e: any) => {
      if (e.shape === "Marker") {
        map.pm.disableDraw("Marker");
        callbacks.onDraw(e.marker.getLatLng());
        setMapMode(MapMode.SELECT);
        map.pm.disableDraw("Marker");
        map.off("pm:create");
      }
    });
    map.pm.enableDraw("Marker", {
      markerStyle: { icon: getLeafletMarkerIcon("new") },
    });

    return function cleanup() {
      map.off("pm:create");
    };
  }

  return {
    mapMode,
    toggleMapMode,
  };
}
